import React from 'react'
import { Tag } from 'antd'
import type { Approval } from '@/types'
import { useTranslation } from 'react-i18next'

export function DocumentPreviewSummary({ record }: { record: Approval }) {
  const { t } = useTranslation(['PendingPOD', 'ReportsPOD', 'UploadPOD'])
  const nz = (v: string | number | null | undefined): React.ReactNode =>
    v != null && String(v).trim() !== '' ? String(v) : '—'

  const storedRows: [string, React.ReactNode][] = [
    [t('ReportsPOD:columns.documentNo', 'Document No') as string, nz(record.documentNo)],
    [
      t('ReportsPOD:details.type', 'Type') as string,
      record.type ? (
        <Tag color={record.type === 'ALT' ? 'blue' : record.type === 'DHR' ? 'green' : record.type === 'GRN' ? 'orange' : 'purple'}>
          {t(`UploadPOD:types.${record.type}`, record.type) as string}
        </Tag>
      ) : (
        '—'
      ),
    ],
    [t('ReportsPOD:details.detailsStatus', 'Status') as string, t(`ReportsPOD:status.${record.status}`, record.status) as string],
    [t('UploadPOD:details.fields.CustomerName', 'Customer') as string, nz(record.customerName)],
    [t('ReportsPOD:details.detailsFromLocation', 'From') as string, nz(record.fromLocation)],
    [t('ReportsPOD:details.detailsToLocation', 'To') as string, nz(record.toLocation)],
    [t('ReportsPOD:details.materialCodeNo', 'Material code') as string, nz(record.materialCodeNo)],
    [t('UploadPOD:details.fields.Year', 'Year') as string, nz(record.year)],
    [t('ReportsPOD:details.detailsPostingDate', 'Posting date') as string, nz(record.postingDate)],
    [t('UploadPOD:details.fields.CustomerCode', 'Customer code') as string, nz(record.customerCode)],
    [t('ReportsPOD:details.detailsQuantity', 'Quantity') as string, nz(record.quantity)],
    [t('UploadPOD:details.fields.ActualQty', 'Actual qty') as string, nz(record.actual_qty)],
    [t('UploadPOD:details.fields.ExcessQty', 'Excess qty') as string, nz(record.excess_qty)],
    [t('UploadPOD:details.fields.ShortQty', 'Short qty') as string, nz(record.short_qty)],
    [t('ReportsPOD:details.vehicleNo', 'Vehicle') as string, nz(record.vehicleNo)],
    [t('UploadPOD:details.fields.LRNo', 'LR No') as string, nz(record.lrNo)],
    [t('UploadPOD:details.fields.TransporterCode', 'Transporter code') as string, nz(record.transporterCode)],
    [t('UploadPOD:details.fields.TransporterName', 'Transporter') as string, nz(record.transporterName)],
  ]

  return (
    <div className="flex w-full shrink-0 flex-col gap-3 text-sm md:w-[min(100%,320px)] md:max-w-[40%] md:border-r md:border-border md:pr-4">
      <div className="font-semibold text-foreground">
        {t('PendingPOD:preview.documentSummary', 'Document summary') as string}
      </div>
      <div className="max-h-[70vh] space-y-2.5 overflow-y-auto pr-1">
        {storedRows.map(([label, val]) => (
          <div key={label}>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="mt-0.5 break-words text-foreground">{val}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
